import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Stripe, StripeElements } from '@stripe/stripe-js'

import AppLayout from '../../../components/app-layout/AppLayout'
import CardSurface from '../../../components/card-surface/CardSurface'
import Button from '../../../components/button/Button'
import { useResidentTenancy } from '../../../_hooks/use-resident-tenancy'
import {
  createAutopaySetup,
  createAutopaySetupIntent,
} from '../../../_data/resident'
import { getStripeClient } from '../../../_utilities/stripe-loader'
import { PAYMENT_ELEMENT_APPEARANCE } from '../../../_defaults/payment-element-appearance'
import { AutopaySetupPageState } from './types'
import {
  AutopayStack,
  AutopayHeader,
  AutopayTitle,
  AutopaySubtitle,
  ExplainerList,
  ExplainerItem,
  ElementContainer,
  SetupActions,
  SetupErrorText,
  QuietNote,
  BackLink,
} from './styled'

/**
 * The in-app autopay enrollment page: a short explainer above an embedded
 * Stripe Payment Element that collects the renter's bank account and the
 * regulated recurring PAD mandate (Stripe renders the mandate agreement — we
 * never write that copy). No money moves here; the saved mandate is captured
 * by the server-side webhook, which arms autopay. When the SetupIntent,
 * Stripe.js, or the publishable key is unavailable, the page silently falls
 * back to the hosted Stripe Checkout setup flow.
 *
 * @returns {JSX.Element}
 */
const AutopaySetupPage = (): JSX.Element => {
  const params = useParams()
  const navigate = useNavigate()
  const { tenancies, isLoadingTenancies } = useResidentTenancy()

  const tenancyId = params.tenancyId ?? ''

  const [pageState, setPageState] = useState<AutopaySetupPageState>('loading')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const stripeRef = useRef<Stripe | null>(null)
  const elementsRef = useRef<StripeElements | null>(null)
  const elementContainerRef = useRef<HTMLDivElement | null>(null)

  const tenancy =
    tenancies.find(candidate => candidate.id === tenancyId) ?? null

  useEffect(() => {
    if (tenancyId === '') {
      navigate('/payments')
      return
    }
    if (isLoadingTenancies === false && tenancy === null) {
      navigate('/payments')
    }
  }, [tenancyId, isLoadingTenancies, tenancy, navigate])

  useEffect(() => {
    if (tenancyId === '') {
      return undefined
    }

    let isActive = true

    const fallBackToHostedSetup = async (): Promise<void> => {
      const checkout = await createAutopaySetup(tenancyId)
      if (isActive === false) {
        return
      }
      if (checkout === null) {
        setPageState('error')
        setErrorMessage(
          'We could not start autopay setup. Please try again.',
        )
        return
      }
      window.location.href = checkout.checkoutUrl
    }

    const setUpEnrollment = async (): Promise<void> => {
      const [setupResult, stripe] = await Promise.all([
        createAutopaySetupIntent(tenancyId),
        getStripeClient(),
      ])
      if (isActive === false) {
        return
      }

      if (
        setupResult === null ||
        stripe === null ||
        elementContainerRef.current === null
      ) {
        await fallBackToHostedSetup()
        return
      }

      stripeRef.current = stripe

      const elements = stripe.elements({
        clientSecret: setupResult.clientSecret,
        appearance: PAYMENT_ELEMENT_APPEARANCE,
      })
      elementsRef.current = elements

      const paymentElement = elements.create('payment')
      paymentElement.mount(elementContainerRef.current)
      setPageState('ready')
    }

    void setUpEnrollment()

    return () => {
      isActive = false
      const elements = elementsRef.current
      if (elements !== null) {
        const paymentElement = elements.getElement('payment')
        if (paymentElement !== null) {
          paymentElement.destroy()
        }
      }
      elementsRef.current = null
      stripeRef.current = null
    }
  }, [tenancyId])

  const handleConfirm = useCallback(async (): Promise<void> => {
    const stripe = stripeRef.current
    const elements = elementsRef.current
    if (stripe === null || elements === null) {
      return
    }

    setErrorMessage('')
    setPageState('confirming')

    const result = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/#/payments?autopay=success`,
      },
      redirect: 'if_required',
    })

    if (result.error !== undefined) {
      setErrorMessage(
        result.error.message ??
          'Autopay setup could not be completed. Please try again.',
      )
      setPageState('ready')
      return
    }

    navigate('/payments?autopay=success')
  }, [navigate])

  let subtitleText = 'Save your bank account once and rent takes care of itself.'
  if (tenancy !== null) {
    subtitleText = `Save your bank account once and rent for ${tenancy.unitLabel} takes care of itself.`
  }

  return (
    <AppLayout contentMaxWidth="standard">
      <AutopayStack>
        <AutopayHeader>
          <AutopayTitle>Set up autopay</AutopayTitle>
          <AutopaySubtitle>{subtitleText}</AutopaySubtitle>
        </AutopayHeader>

        <CardSurface padding="standard">
          <ExplainerList>
            <ExplainerItem>
              Your rent is charged automatically each month from your bank
              account, with no card fee.
            </ExplainerItem>
            <ExplainerItem>
              Nothing is charged today. You are saving your bank details and
              authorizing the monthly payment.
            </ExplainerItem>
            <ExplainerItem>
              You can turn autopay off any time from the Payments page.
            </ExplainerItem>
          </ExplainerList>

          {pageState === 'loading' ? (
            <QuietNote>Preparing autopay setup...</QuietNote>
          ) : null}

          <ElementContainer ref={elementContainerRef} />

          {errorMessage !== '' ? (
            <SetupErrorText>{errorMessage}</SetupErrorText>
          ) : null}

          {pageState === 'ready' || pageState === 'confirming' ? (
            <SetupActions>
              <Button
                type="button"
                variant="primary"
                size="small"
                isLoading={pageState === 'confirming'}
                disabled={pageState === 'confirming'}
                onClick={() => {
                  void handleConfirm()
                }}
              >
                Save and turn on autopay
              </Button>
              <BackLink
                type="button"
                onClick={() => {
                  navigate('/payments')
                }}
              >
                Back to payments
              </BackLink>
            </SetupActions>
          ) : null}

          {pageState === 'error' ? (
            <SetupActions>
              <BackLink
                type="button"
                onClick={() => {
                  navigate('/payments')
                }}
              >
                Back to payments
              </BackLink>
            </SetupActions>
          ) : null}
        </CardSurface>
      </AutopayStack>
    </AppLayout>
  )
}

export default AutopaySetupPage
